// src/db/assetDB.js
// Helper CRUD untuk store 'assets' dan 'asset_pengajuan' di IndexedDB

import { initDB } from './db';

const SYNC_META_KEY = 'asset_last_sync';

// ── Assets ──────────────────────────────────────────────────────────────────

/** Simpan seluruh list asset ke IDB (upsert by kode) */
export const saveAllAssets = async (assets = []) => {
  try {
    const db = await initDB();
    const tx = db.transaction('assets', 'readwrite');
    await Promise.all([
      ...assets.map(a => tx.store.put(a)),
      tx.done,
    ]);
    localStorage.setItem(SYNC_META_KEY, new Date().toISOString());
  } catch (e) {
    console.error('[assetDB] saveAllAssets error:', e);
  }
};

/** Ambil semua asset dari IDB */
export const getAllAssets = async () => {
  try {
    const db = await initDB();
    return await db.getAll('assets');
  } catch (e) {
    console.error('[assetDB] getAllAssets error:', e);
    return [];
  }
};

/** Cari asset berdasarkan kode (exact match) */
export const getAssetByKode = async (kode) => {
  try {
    const db = await initDB();
    return await db.get('assets', kode);
  } catch (e) {
    console.error('[assetDB] getAssetByKode error:', e);
    return null;
  }
};

/** Cari asset berdasarkan query string (nama atau kode, case-insensitive) */
export const searchAssets = async (query = '') => {
  try {
    const db = await initDB();
    const all = await db.getAll('assets');
    if (!query.trim()) return all;
    const q = query.toLowerCase();
    return all.filter(a =>
      (a.kode || '').toLowerCase().includes(q) ||
      (a.nama || '').toLowerCase().includes(q) ||
      (a.kategori || '').toLowerCase().includes(q) ||
      (a.lokasi || '').toLowerCase().includes(q)
    );
  } catch (e) {
    console.error('[assetDB] searchAssets error:', e);
    return [];
  }
};

/** Hapus semua data asset dari IDB */
export const clearAssets = async () => {
  try {
    const db = await initDB();
    await db.clear('assets');
  } catch (e) {
    console.error('[assetDB] clearAssets error:', e);
  }
};

/** Update lokasi asset secara lokal di cache */
export const updateAssetLocation = async (kode, lokasi) => {
  try {
    const db = await initDB();
    const tx = db.transaction('assets', 'readwrite');
    const asset = await tx.store.get(kode);
    if (asset) {
      asset.lokasi = lokasi;
      await tx.store.put(asset);
    }
    await tx.done;
  } catch (e) {
    console.error('[assetDB] updateAssetLocation error:', e);
  }
};

/** Waktu terakhir sync (dari localStorage meta) */
export const getLastSyncTime = () => {
  const raw = localStorage.getItem(SYNC_META_KEY);
  if (!raw) return null;
  try { return new Date(raw); } catch { return null; }
};

// ── Pengajuan ────────────────────────────────────────────────────────────────

/**
 * Simpan pengajuan baru ke IDB
 * @param {object} data - { jenis, kode_asset, nama_asset, deskripsi, karyawan_id, karyawan_nama, ... }
 * @returns {number} id pengajuan yang baru dibuat
 */
export const savePengajuan = async (data) => {
  try {
    const db = await initDB();
    const payload = {
      ...data,
      status: 'Pending',
      created_at: new Date().toISOString(),
      synced: false,
    };
    const id = await db.add('asset_pengajuan', payload);
    return id;
  } catch (e) {
    console.error('[assetDB] savePengajuan error:', e);
    throw e;
  }
};

/** Ambil semua pengajuan dari IDB */
export const getAllPengajuan = async () => {
  try {
    const db = await initDB();
    const all = await db.getAll('asset_pengajuan');
    return all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } catch (e) {
    console.error('[assetDB] getAllPengajuan error:', e);
    return [];
  }
};

/** Update status pengajuan (misal setelah berhasil sync ke server) */
export const updatePengajuanStatus = async (id, status, synced = true) => {
  try {
    const db = await initDB();
    const existing = await db.get('asset_pengajuan', id);
    if (!existing) return;
    await db.put('asset_pengajuan', { ...existing, status, synced });
  } catch (e) {
    console.error('[assetDB] updatePengajuanStatus error:', e);
  }
};

/** Hapus pengajuan by id */
export const deletePengajuan = async (id) => {
  try {
    const db = await initDB();
    await db.delete('asset_pengajuan', id);
  } catch (e) {
    console.error('[assetDB] deletePengajuan error:', e);
  }
};
